// src/components/PartnerPortal.js
import React, { useState } from "react";
import "../styles/PartnerPortal.css";
import AuraKartLogo from "../assets/Aurakart_logo.jpg";
import OffersAndBanners from "./Admin/OffersAndBanners/OffersAndBanners";
import Orders from "./Orders";
import Customers from "./Customers";
import Products from "./ProductManagement/Products";
import Stores from "./Stores";
import Vendors from "./Vendors";
import Sellers from "./Sellers";
import Inventory from "./Inventory";
import SellerApplications from "./SellerApplications";

import SellerOrders from "./Seller/SellerOrders";
import SellerProducts from "./Seller/SellerProducts";
import SellerInventory from "./Seller/SellerInventory";
import SellerPayouts from "./Seller/SellerPayouts";
import SellerProfile from "./Seller/SellerProfile";
import SellerDashboard from "./Seller/SellerDashboard"; // ✅ NEW
import ChangePassword from "./ChangePassword";

// ✅ ADMIN components
import AdminPayOuts from "./Admin/AdminPayOuts";
import AdminCommissions from "./Admin/AdminCommissions";

export default function PartnerPortal({
  userRole,
  sellerId,
  onLogout,
  sellerOnboardingStatus = "APPROVED",
  application,
  onboardingMessage,
  onStartRegistration,
}) {
  const [activeMenu, setActiveMenu] = useState(
    userRole === "seller" ? "seller-profile" : "dashboard"
  );
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // which seller we are inspecting payouts for (admin view)
  const [selectedSellerForPayouts, setSelectedSellerForPayouts] =
    useState(null);

  const adminMenuItems = [
    { id: "dashboard", label: "📊 Dashboard", icon: "📊" },
    { id: "orders", label: "📦 Orders", icon: "📦" },
    { id: "customers", label: "👥 Customers", icon: "👥" },
    { id: "products", label: "🛍️ Products", icon: "🛍️" },
    { id: "offers-banners", label: "🏷️ Offers & Banners", icon: "🏷️" },
    { id: "inventory", label: "📋 Inventory", icon: "📋" },
    { id: "stores", label: "🏬 Stores", icon: "🏬" },
    { id: "vendors", label: "🤝 Vendors", icon: "🤝" },
    { id: "sellers", label: "👨‍💼 Sellers", icon: "👨‍💼" },
    { id: "seller-applications", label: "📋 Seller Applications", icon: "📋" },
    { id: "seller-payouts", label: "💰 Seller Payouts", icon: "💰" },
    { id: "commissions", label: "📈 Commissions", icon: "📈" },
  ];

  const sellerMenuItems = [
    { id: "seller-profile", label: "👤 Profile", icon: "👤" },
    { id: "dashboard", label: "📊 Dashboard", icon: "📊" },
    { id: "seller-orders", label: "📦 Orders", icon: "📦" },
    { id: "seller-products", label: "🛍️ Products", icon: "🛍️" },
    { id: "seller-inventory", label: "📋 Inventory", icon: "📋" },
    { id: "seller-payouts", label: "💰 Payouts", icon: "💰" },
  ];

  const menuItems = userRole === "admin" ? adminMenuItems : sellerMenuItems;

  const isSeller = userRole === "seller";
  const isSellerApproved = isSeller && sellerOnboardingStatus === "APPROVED";

  const renderSellerLocked = () => {
    const status = sellerOnboardingStatus || "NONE";

    return (
      <div className="dashboard-placeholder">
        <h2>Seller Onboarding</h2>

        {onboardingMessage && (
          <p style={{ whiteSpace: "pre-line", marginBottom: 12 }}>
            {onboardingMessage}
          </p>
        )}

        {status === "NONE" && (
          <>
            <p>
              You are a few steps away from listing your products on AuraKart
              and starting to sell.
            </p>
            {onStartRegistration && (
              <button
                className="change-password-btn-header"
                onClick={onStartRegistration}
              >
                Complete Seller Registration
              </button>
            )}
          </>
        )}

        {status === "PENDING" && (
          <p>Your seller application is under review. Please check back later.</p>
        )}

        {status === "REJECTED" && (
          <>
            <p>
              Your application was rejected. Please update your details (for
              example GST / bank details) and re-apply.
            </p>
            {application?.adminComment && (
              <p style={{ marginTop: 8, color: "#b91c1c" }}>
                <strong>Reason:</strong> {application.adminComment}
              </p>
            )}
            {onStartRegistration && (
              <button
                className="change-password-btn-header"
                onClick={onStartRegistration}
              >
                Update Details &amp; Re-Apply
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const renderContent = () => {
    // ADMIN VIEWS
    if (userRole === "admin") {
      switch (activeMenu) {
        case "dashboard":
          return (
            <div className="dashboard-placeholder">
              <h2>Welcome to AuraKart Admin Dashboard</h2>
              <p>Select a menu item to get started</p>
            </div>
          );
        case "orders":
          return <Orders />;
        case "customers":
          return <Customers />;
        case "products":
          return <Products />;
        case "offers-banners":
          return <OffersAndBanners />;
        case "inventory":
          return <Inventory />;
        case "stores":
          return <Stores />;
        case "vendors":
          return <Vendors />;
        case "sellers":
          return (
            <Sellers
              onViewPayouts={(id, name) => {
                setSelectedSellerForPayouts({ id, name });
                setActiveMenu("seller-payouts");
              }}
            />
          );
        case "seller-applications":
          return <SellerApplications />;
        case "seller-payouts":
          return (
            <AdminPayOuts
              sellerId={selectedSellerForPayouts?.id || null}
              sellerName={selectedSellerForPayouts?.name || ""}
            />
          );
        case "commissions":
          return <AdminCommissions />;
        default:
          return null;
      }
    }

    // SELLER VIEWS
    if (userRole === "seller") {
      if (!isSellerApproved) {
        return renderSellerLocked();
      }

      switch (activeMenu) {
        case "seller-profile":
          return <SellerProfile />;
        case "dashboard":
          return <SellerDashboard sellerId={sellerId} />; // ✅ use new dashboard
        case "seller-orders":
          return <SellerOrders sellerId={sellerId} />;
        case "seller-products":
          return <SellerProducts sellerId={sellerId} />;
        case "seller-inventory":
          return <SellerInventory sellerId={sellerId} />;
        case "seller-payouts":
          return <SellerPayouts sellerId={sellerId} />;
        default:
          return null;
      }
    }

    return null;
  };

  return (
    <div className="partner-portal">
      {/* TOP NAVBAR */}
      <div className="navbar-header">
        <div className="navbar-brand">
          <img src={AuraKartLogo} alt="AuraKart" className="navbar-logo" />
          <h1 className="navbar-title">AuraKart Partner Portal</h1>
        </div>

        <div className="navbar-actions">
          <span className="user-badge">
            {userRole === "admin" ? "👨‍💼 Admin" : "🏪 Seller"}
          </span>
          {userRole === "seller" && (
            <button
              className="change-password-btn-header"
              onClick={() => setShowChangePassword(true)}
              title="Change your password"
              disabled={!isSellerApproved}
            >
              🔐 Change Password
            </button>
          )}
          <button className="logout-btn-header" onClick={onLogout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* MENU NAVBAR */}
      <nav className="navbar-menu-row">
        <div className={`navbar-menu ${mobileMenuOpen ? "open" : ""}`}>
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`navbar-item ${activeMenu === item.id ? "active" : ""
                }`}
              onClick={() => {
                if (item.id === "seller-payouts") {
                  setSelectedSellerForPayouts(null);
                }
                setActiveMenu(item.id);
                setMobileMenuOpen(false);
              }}
              title={item.label}
            >
              <span className="menu-icon">{item.icon}</span>
              <span className="menu-label">{item.label}</span>
            </button>
          ))}
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          ☰
        </button>
      </nav>

      {/* MAIN CONTENT */}
      <div className="main-content-horizontal">
        <div className="content-body">{renderContent()}</div>
      </div>

      {/* CHANGE PASSWORD MODAL */}
      {showChangePassword && userRole === "seller" && isSellerApproved && (
        <ChangePassword
          onClose={() => setShowChangePassword(false)}
          sellerId={sellerId}
        />
      )}
    </div>
  );
}
